import { Button } from '@vrplatform/components';
import {
  captureSentryError,
  type gqlV2,
  useBilling,
  useGqtyClient,
  useTrackingEvent,
  useTeam,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { showWarnNotification } from '@vrplatform/theme';
import { useAppName } from '@vrplatform/theme';
import { ensure } from '@vrplatform/utils';
import { Center, Menu } from '@mantine/core';
import {
  defaultAccountOrderBy,
  type recurringFeeType_enum,
} from '@vrplatform/graphql';
import { type ReactNode, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import type { FeeFormInputs } from '../../edit/useFeeForm';
import { FEE_TEMPLATES } from '../_constants';
import { FEE_TYPES } from './FeeTypeCarousel';

export const AddFeeButton = ({
  children = 'Add fee',
  width,
}: {
  children?: ReactNode;
  width?: number;
}) => {
  const { isReadOnly } = useBilling();

  const { loading, mutate } = useAddRecurringFee();

  const goToAdd = useCallback(
    (template: recurringFeeType_enum) => mutate(template),
    [mutate]
  );

  if (isReadOnly) return null;

  return (
    <Menu trigger="click" shadow="md" position="bottom-end" withArrow>
      <Menu.Target>
        <Button
          variant="primary"
          leftIcon={'PlusIcon'}
          loading={loading}
          sx={{
            width,
          }}
        >
          {children}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {Object.entries(FEE_TYPES).map(([key, value]) => {
          return (
            <Menu.Item
              key={key}
              leftSection={
                <Center
                  w={20}
                  sx={{
                    justifyContent: 'flex-start',
                  }}
                >
                  <Icon
                    icon={value.icon}
                    // color={(theme) => theme.colors[theme.primaryColor][6]}
                    size={18}
                  />
                </Center>
              }
              onClick={() => {
                const fee = Object.entries(FEE_TEMPLATES).find(
                  ([, v]) => value.type === v.formInputs.type
                );
                if (fee) {
                  goToAdd(fee[0] as recurringFeeType_enum);
                } else {
                  showWarnNotification({
                    title: 'Fee type not found',
                    message: `The fee type ${key} was not found in the template list.`,
                  });
                }
              }}
            >
              {value.title.slice(0, -1)}
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
};

const useAddRecurringFee = () => {
  const goto = useNavigate();
  const [{ id: teamId }] = useTeam();
  const [loading, setLoading] = useState(false);
  const { appName } = useAppName();

  const track = useTrackingEvent();

  const client = useGqtyClient();

  const mutate = useCallback(
    async (templateKey: recurringFeeType_enum) => {
      try {
        setLoading(true);

        const template = FEE_TEMPLATES[templateKey];

        if (!template) throw new Error('Template not found');

        // get more info
        const queryData = await client.query((q) => {
          const getAccount = (account: gqlV2.account) => ({
            id: account.id,
            title: account.title!,
          });

          let debitCreditAccounts: ReturnType<typeof getAccount>[] = [];

          const formulaAccounts = q
            .accounts({
              where: {
                tenantId: { _eq: teamId },
                title: { _in: template.config.accounts },
              },
              order_by: defaultAccountOrderBy,
            })
            .map(getAccount);

          const debitCreditAccountIds = [
            template.formInputs.debitAccountId,
            template.formInputs.creditAccountId,
          ].filter((x) => x !== 'create_new');

          if (debitCreditAccountIds.length) {
            debitCreditAccounts = q
              .accounts({
                where: {
                  tenantId: { _eq: teamId },
                  title: { _in: debitCreditAccountIds },
                },
                order_by: defaultAccountOrderBy,
              })
              .map(getAccount);
          }

          const defaultRecognition =
            q.tenantById({
              id: teamId,
            })?.defaultRevenueRecognition || 'checkIn';

          return {
            formulaAccounts,
            debitCreditAccounts,
            defaultRecognition,
          };
        });

        if (
          queryData.formulaAccounts.length !== template.config.accounts.length
        ) {
          const missingAccounts = template.config.accounts.filter(
            (account) =>
              !queryData.formulaAccounts.find((a) => a.title === account)
          );

          track('recurring_fee_missing_template_account', {
            accounts: missingAccounts,
            type: templateKey,
          });

          showWarnNotification({
            title: 'Missing template accounts',
            message: `The following accounts are missing from your team for the ${appName} default template: ${missingAccounts.join(', ')}`,
          });
        }

        goto('/fees-commissions/add', {
          state: ensure<FeeFormInputs>({
            ...template.formInputs,
            linkedAccountIds: queryData.formulaAccounts.map((a) => a.id),
            creditAccountId:
              queryData.debitCreditAccounts.find(
                (x) => x.title === template.formInputs.creditAccountId
              )?.id || 'create_new',
            debitAccountId:
              queryData.debitCreditAccounts.find(
                (x) => x.title === template.formInputs.debitAccountId
              )?.id || 'create_new',
            revenueRecognition: queryData.defaultRecognition,
          }),
        });
      } catch (error: any) {
        captureSentryError(error);
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [goto, teamId, client, appName, track]
  );

  return {
    mutate,
    loading,
  };
};
